import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Brain, Lightbulb, Target, Users, Heart } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";

interface ReflectPhaseProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  eventTitle: string;
}

export const ReflectPhase: React.FC<ReflectPhaseProps> = ({ isOpen, onClose, onSave, eventTitle }) => {
  const [activeTab, setActiveTab] = useState('patterns');
  const [responses, setResponses] = useState({
    // Pattern Recognition
    emotionalPattern: '',
    behavioralPattern: '',
    thoughtPattern: '',
    
    // Core Beliefs
    beliefAboutSelf: '',
    beliefAboutOthers: '',
    beliefAboutWorld: '',
    
    // Impact Analysis
    presentDayTriggers: '',
    relationshipImpact: '',
    decisionMakingImpact: '',
    
    // Meaning Making
    meaningGiven: '',
    protectiveFunction: '',
    adaptiveStrategy: '',
    
    // Levels Affected
    physicalLevel: '',
    emotionalLevel: '',
    mentalLevel: '',
    spiritualLevel: '',
    relationalLevel: '',
  });

  const handleSave = () => {
    onSave({
      reflection_notes: JSON.stringify(responses),
      reflection_completed: true
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Brain className="h-6 w-6 text-primary" />
            Reflect Phase: {eventTitle}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Deep psychological reflection using evidence-based therapeutic frameworks
          </p>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="patterns">Patterns</TabsTrigger>
            <TabsTrigger value="beliefs">Core Beliefs</TabsTrigger>
            <TabsTrigger value="impact">Impact</TabsTrigger>
            <TabsTrigger value="levels">Levels</TabsTrigger>
          </TabsList>

          <TabsContent value="patterns" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-primary" />
                Pattern Recognition (CBT Framework)
              </h3>
              <p className="text-xs text-muted-foreground">
                Identify recurring patterns in your emotional, behavioral, and cognitive responses
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Emotional Pattern</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What emotion(s) did you feel then? Do you still feel them when triggered? (e.g., shame, fear, anger, abandonment)
                </p>
                <Textarea
                  value={responses.emotionalPattern}
                  onChange={(e) => setResponses({ ...responses, emotionalPattern: e.target.value })}
                  rows={3}
                  placeholder="Example: I felt deep shame and unworthiness. Even now, when criticized, I immediately feel that same crushing shame..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Behavioral Pattern</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What did you do in response? Do you repeat these behaviours? (withdrawal, people-pleasing, perfectionism, avoidance)
                </p>
                <Textarea
                  value={responses.behavioralPattern}
                  onChange={(e) => setResponses({ ...responses, behavioralPattern: e.target.value })}
                  rows={3}
                  placeholder="Example: I withdrew and became hypervigilant about making mistakes. Now I overwork to prove my worth..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Thought Pattern (Cognitive Schema)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What automatic thoughts arose? What story did your mind create? (catastrophizing, all-or-nothing thinking, mind reading)
                </p>
                <Textarea
                  value={responses.thoughtPattern}
                  onChange={(e) => setResponses({ ...responses, thoughtPattern: e.target.value })}
                  rows={3}
                  placeholder="Example: 'I'm not good enough. If I'm not perfect, I'll be abandoned.' This became my inner critic's default narrative..."
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="beliefs" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                Core Belief Identification (Schema Therapy)
              </h3>
              <p className="text-xs text-muted-foreground">
                Uncover the deep beliefs formed about yourself, others, and the world
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Belief About Self</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What conclusion did you draw about who you are? (identity, worth, capability, lovability)
                </p>
                <Textarea
                  value={responses.beliefAboutSelf}
                  onChange={(e) => setResponses({ ...responses, beliefAboutSelf: e.target.value })}
                  rows={3}
                  placeholder="Example: 'I am fundamentally flawed. I don't deserve love unless I'm perfect. My needs don't matter.'"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Belief About Others</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What did you learn about people, relationships, trust, safety?
                </p>
                <Textarea
                  value={responses.beliefAboutOthers}
                  onChange={(e) => setResponses({ ...responses, beliefAboutOthers: e.target.value })}
                  rows={3}
                  placeholder="Example: 'People will leave if they see the real me. Love is conditional. I can't trust anyone with my vulnerability.'"
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Belief About the World</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What worldview formed? (safety, fairness, predictability, abundance/scarcity)
                </p>
                <Textarea
                  value={responses.beliefAboutWorld}
                  onChange={(e) => setResponses({ ...responses, beliefAboutWorld: e.target.value })}
                  rows={3}
                  placeholder="Example: 'The world is dangerous and unpredictable. There's not enough (love, success, safety) to go around. I must always be on guard.'"
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="impact" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                Present-Day Impact Analysis
              </h3>
              <p className="text-xs text-muted-foreground">
                Understand how this past event influences your current life
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Triggers & Somatic Responses</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What situations activate this memory? What do you feel in your body? (tightness, nausea, numbness, hyperarousal)
                </p>
                <Textarea
                  value={responses.presentDayTriggers}
                  onChange={(e) => setResponses({ ...responses, presentDayTriggers: e.target.value })}
                  rows={3}
                  placeholder="Example: When my partner is quiet, my chest tightens and I feel 8 years old again. My stomach drops when I receive any criticism at work..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Relationship Patterns</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  How does this show up in your relationships? (attachment style, boundaries, communication, intimacy)
                </p>
                <Textarea
                  value={responses.relationshipImpact}
                  onChange={(e) => setResponses({ ...responses, relationshipImpact: e.target.value })}
                  rows={3}
                  placeholder="Example: I push people away before they can leave me. I over-function in relationships to feel needed. I'm terrified of being truly seen..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Decision-Making & Life Choices</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What opportunities have you avoided? What paths did you not take because of this belief?
                </p>
                <Textarea
                  value={responses.decisionMakingImpact}
                  onChange={(e) => setResponses({ ...responses, decisionMakingImpact: e.target.value })}
                  rows={3}
                  placeholder="Example: I've stayed small professionally to avoid visibility. I chose partners who confirmed my unworthiness. I've said no to joy because I don't trust it..."
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="levels" className="space-y-6 mt-6">
            <Card className="p-4 bg-primary/5 border-primary/20">
              <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
                <Heart className="h-4 w-4 text-primary" />
                Multi-Level Impact (Holistic Framework)
              </h3>
              <p className="text-xs text-muted-foreground">
                Explore how this event affected different dimensions of your being
              </p>
            </Card>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Physical Level</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Chronic tension, illness, energy depletion, nervous system dysregulation
                </p>
                <Textarea
                  value={responses.physicalLevel}
                  onChange={(e) => setResponses({ ...responses, physicalLevel: e.target.value })}
                  rows={2}
                  placeholder="Example: Chronic upper back tension, digestive issues when stressed, constant fatigue..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Emotional Level</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Emotional regulation, range of feelings, numbness, overwhelm
                </p>
                <Textarea
                  value={responses.emotionalLevel}
                  onChange={(e) => setResponses({ ...responses, emotionalLevel: e.target.value })}
                  rows={2}
                  placeholder="Example: Difficulty accessing joy, quick to rage, chronically anxious, emotionally numb..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Mental Level</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Cognitive patterns, beliefs, self-talk, mental clarity
                </p>
                <Textarea
                  value={responses.mentalLevel}
                  onChange={(e) => setResponses({ ...responses, mentalLevel: e.target.value })}
                  rows={2}
                  placeholder="Example: Constant negative self-talk, obsessive rumination, black-and-white thinking..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Spiritual Level</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Connection to meaning, purpose, trust in life, sense of belonging
                </p>
                <Textarea
                  value={responses.spiritualLevel}
                  onChange={(e) => setResponses({ ...responses, spiritualLevel: e.target.value })}
                  rows={2}
                  placeholder="Example: Lost sense of purpose, disconnected from intuition, don't trust the universe..."
                />
              </div>

              <div>
                <Label className="text-sm font-medium">Relational Level</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Connection, vulnerability, boundaries, intimacy capacity
                </p>
                <Textarea
                  value={responses.relationalLevel}
                  onChange={(e) => setResponses({ ...responses, relationalLevel: e.target.value })}
                  rows={2}
                  placeholder="Example: Walls up, fear of vulnerability, enmeshed boundaries, isolated..."
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Reflection
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};